import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import logo from '../assets/Screenshot 2025-07-05 200950.png';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navLinks = [
    { href: '#home', label: 'Home' },
    { href: '#about', label: 'About Us' },
    { href: '#approach', label: 'Our Approach' },
    { href: '#programs', label: 'Programs' },
    { href: '#events', label: 'Events' },
    { href: '#testimonials', label: 'Testimonials' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#contact', label: 'Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      const headerHeight = 80; // Approximate header height
      const targetPosition = targetElement.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
    
    setIsMenuOpen(false);
  };

  return (
    <motion.header 
      className={`sticky top-0  z-50 transition-all duration-300 bg-brand-white shadow-bubble ${isScrolled ? 'shadow-brand' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      
      <nav className="px-16 py-4 mx-0 flex items-center justify-between w-full">
        {/* Logo and Brand Name */}
        <motion.div 
          className="flex items-center space-x-4"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <img src={logo} alt="Neev Play School Logo" className="h-14 w-14 rounded-full shadow-bubble object-contain" />
          {/* <span className="text-2xl font-bold text-brand-pink tracking-tight" style={{ fontFamily: 'Nunito, Quicksand, Varela Round, sans-serif' }}>
            Neev <span className="text-brand-blue">Play School</span>
          </span> */}
        </motion.div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => (
            <motion.a
              key={link.href}
              href={link.href}
              onClick={(e) => handleSmoothScroll(e, link.href)}
              className="text-brand-pink  hover:text-brand-yellow transition-colors duration-200 font-bold px-2 py-1 rounded-bubble"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {link.label}
            </motion.a>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <motion.button
          className="md:hidden p-2 text-brand-pink hover:text-brand-yellow text-2xl rounded-lg hover:bg-brand-yellow/10 transition-all duration-200"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.05 }}
        >
          {isMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
        </motion.button>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <>
              {/* Backdrop Overlay */}
              <motion.div
                className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMenuOpen(false)}
                transition={{ duration: 0.2 }}
              />
              
              {/* Menu Dropdown */}
              <motion.div
                className="md:hidden fixed top-[72px] right-4 w-64 bg-white shadow-xl rounded-lg overflow-hidden border border-gray-100 z-50"
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <div className="py-2">
                  {navLinks.map((link, index) => (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleSmoothScroll(e, link.href)}
                      className="block text-brand-pink hover:bg-brand-yellow/10 hover:text-brand-yellow transition-all duration-200 font-semibold py-3 px-6 border-b border-gray-50 last:border-b-0"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      {link.label}
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header; 